import { Form, Radio, Select } from 'antd';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { FV } from './share';
import ErpSku from './ErpSku';
import StoreItemList, { Item } from './StoreItemList';
import ErpItemList from './ErpItemList';
import { useTranslate } from '@tolgee/react';
import FormField from 'components/FormField';
import styled from '@emotion/styled';
import { useRequest } from 'ahooks';
import { productList } from 'services/api/conversation';
import { LoadingOutlined } from '@ant-design/icons';
import SafeImage from 'components/SafeImage';

function ItemInfo({
  disabled,
  storeId,
  itemOffsale,
  item,
  skuInfo,
}: {
  disabled?: boolean;
  storeId: number;
  itemOffsale?: boolean;
  item: Pick<Conversation.Trade.Product, 'externalItemId' | 'itemPic' | 'itemName'>;
  skuInfo: Pick<Conversation.ProductSKUInfo, 'sku' | 'skuId' | 'sellerSku'>[];
}) {
  const { t } = useTranslate();

  const [type, setType] = useState<'STORE_ITEM' | 'ERP_ITEM'>('STORE_ITEM');

  const {
    values,
    values: { skus, skuId },
    setValues,
  } = useFormikContext<FV>();

  const spuIds = useMemo<string[]>(() => {
    if (!disabled) {
      return [];
    }
    return (skus?.map((_) => _?.relatedSpuId).filter((_) => _) as string[]) || [];
  }, [disabled, skus]);

  const { loading, data } = useRequest(
    () => {
      return productList({
        internal_store_id: storeId,
        page_no: 1,
        page_size: spuIds.length,
        external_item_ids: spuIds.join(','),
      }).then((res) => res.itemList);
    },
    {
      ready: spuIds.length > 0,
    },
  );

  if (disabled) {
    return (
      <Form.Item required label={t('补发商品')}>
        {loading ? (
          <LoadingOutlined />
        ) : (
          <Container>
            {skus?.map(({ relatedSpuId, relatedSkuId }, index) => {
              const targetSpu = data?.find(({ externalItemId }) => externalItemId === relatedSpuId);
              const targetSku = targetSpu?.skuInfo.find(({ skuId }) => skuId === relatedSkuId);
              if (!targetSpu || !targetSku) {
                return <ErpSku index={index} />;
              }
              return (
                <div>
                  <Form.Item required label={`${t('店铺')} SPU`}>
                    <Item>
                      <div>
                        <SafeImage
                          hasSmallPic="both"
                          src={targetSpu.imageUrl[0]}
                          width={48}
                          height={48}
                        />
                      </div>
                      <section>{targetSpu.itemName}</section>
                    </Item>
                  </Form.Item>
                  <Form.Item required label={`${t('店铺')} SKU | ${t('SKU货号')}`}>
                    <Select
                      disabled={disabled}
                      dropdownMatchSelectWidth={false}
                      showSearch
                      optionFilterProp="label"
                      placeholder={t('department.请选择x', { x: 'SKU' })}
                      value={relatedSkuId}
                      options={[
                        {
                          value: relatedSkuId,
                          label: `${targetSku.sku} | ${targetSku.sellerSku}`,
                        },
                      ]}
                    />
                  </Form.Item>
                  <ErpSku index={index} />
                </div>
              );
            })}
          </Container>
        )}
      </Form.Item>
    );
  }

  return (
    <FormField<FV['skus']>
      name="skus"
      required
      label={t('补发商品')}
      validate={(skus) =>
        skus?.filter((_) => _).length ? undefined : t('department.请选择x', { x: t('补发商品') })
      }
    >
      <div>
        <Radio
          checked={type === 'STORE_ITEM'}
          onChange={({ target: { checked } }) => {
            if (checked) {
              setType('STORE_ITEM');
              setValues({
                ...values,
                skus: [],
              });
            }
          }}
        >
          {t('通过店铺商品匹配ERP系统商品')}
        </Radio>
        <Radio
          checked={type === 'ERP_ITEM'}
          onChange={({ target: { checked } }) => {
            if (checked) {
              setType('ERP_ITEM');
              setValues({
                ...values,
                skus: [],
              });
            }
          }}
        >
          {t('搜索ERP系统商品')}
        </Radio>
      </div>
      {type === 'STORE_ITEM' && (
        <StoreItemList
          storeId={storeId}
          itemOffsale={itemOffsale}
          item={item}
          skuInfo={skuInfo}
          skuId={skuId || undefined}
        />
      )}
      {type === 'ERP_ITEM' && <ErpItemList storeId={storeId} />}
    </FormField>
  );
}

const Container = styled.div`
  max-height: 500px;
  overflow: auto;
  > * {
    padding: 16px;
    margin-bottom: 16px;
    background: #f7f9fa;
  }
`;

export default ItemInfo;
